import { ResourcelessPermissionId } from '@upright/common/iam/privilege/permission/types';
import { useHasResourcelessPrivilege } from '@upright/ui/api/iam/privilege/query-options';
import { useIsTeam, useViewSetting } from '@upright/ui/common/generic/hooks';
import { useUnifiedShellAudience } from './use-unified-shell-audience';

/**
 * Whether the current user gets the unified shell on the investor side — the white
 * top strip, the Primer-pane rail and the launcher landing at `/` — instead of the
 * dark `BouToolHeader`, `BouPageWithFrame` and the assets table (Unified UI Phase 1,
 * PLT-6530).
 *
 * This is the one gate the header switch (`root/main/header-maybe.tsx`), the page
 * frame and `LoggedInHomeViewSwitch` (`home-views/index.tsx`) all read, so the three
 * cannot disagree about which shell a user is in. Do not re-derive the decision at a
 * call site from `organization.type` or a user group.
 *
 * The company front page takes the same shell (PLT-6943): the launcher at `/` with company
 * prompts, a *My company* page for the old company dashboard, and a company variant of the rail
 * (`UnifiedCompanyNavigation`). Only in an organisation of type Company and never for a DMA user,
 * which `useUnifiedShellAudience` decides; a user in both kinds of group has the investor front
 * page and gets the investor shell, as before.
 *
 * Three answers:
 * - No audience (DMA organisations, self-signup companies, unaffiliated users, and company-group
 *   users outside a Company-type organisation): `false`, without asking for the privilege. Those
 *   shells are their own and this gate does not touch them.
 * - Team users: the team-only `internalAdmin.unifiedShell` View setting, on by default
 *   (PLT-7106). A team user who wants to see what a not-yet-opted-in customer sees turns
 *   it off (the row in the start menu at the bottom left, or View settings), and that
 *   choice is remembered. The setting is the whole gate for the team: the privilege's
 *   team-shortcut would answer `true` unconditionally, which is why team users are
 *   resolved first and the privilege is not consulted for them.
 * - Everyone else: the `USE_UNIFIED_SHELL` resourceless privilege, granted per
 *   organisation from the privileges admin UI.
 *
 * `loading` is `true` while a customer's privilege query is in flight. A consumer that
 * would otherwise paint the old chrome and swap it out a moment later renders nothing
 * until it resolves. The team and no-audience answers never load.
 */
export function useUnifiedShell(): { enabled: boolean; loading: boolean } {
  const audience = useUnifiedShellAudience();
  const isTeam = useIsTeam();
  const unifiedShellForTeam = useViewSetting('internalAdmin.unifiedShell');
  const { hasPrivilege, loading } = useHasResourcelessPrivilege(ResourcelessPermissionId.USE_UNIFIED_SHELL);

  if (audience === null) {
    return { enabled: false, loading: false };
  }
  if (isTeam) {
    return { enabled: unifiedShellForTeam, loading: false };
  }
  return { enabled: hasPrivilege, loading };
}
